/**
 * 山口県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 12品すべてが農水省「うちの郷土料理」山口県の30品から採ってある。
 *
 * ふぐの扱いについて。
 *  ふぐは福岡（Lv12 とらふぐ てっさ）、福井（Lv11 若狭ふぐ）、静岡（Lv12 遠州灘とらふぐ）で
 *  すでに3回、いずれも薄造りの刺身として描いている。4回目の刺身は出さない。
 *  山口では農水省に独立した項目がある「ふぐの唐揚げ」を採り、骨つきの揚げ物として描く。
 *  地理的表示（GI）第19号「下関ふく」の生産地は「山口県下関市及び福岡県北九州市門司区」で、
 *  加工地基準のため福岡県の一部を含む。だから「山口だけのもの」とは書かない。
 *  下関でふぐを「ふく」と呼ぶことは農水省に書かれているので、短縮名に使っている。
 *
 * 形のばらつき。
 *  棒状2（夏みかん菓子の短冊・ごぼう巻き）／平たい板と角柱2（外郎・岩国寿司）／
 *  横に広がる1（瓦そば）／葉が散る1（ちしゃなます）／三角1（わかめむすび）。
 *  器を描く例外は瓦そばだけ。瓦そのものが料理の本体なので外せない。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「日本三名橋」（錦帯橋）「日本三名塔」（瑠璃光寺）「日本三大カルスト」（秋吉台）は
 *    いずれも国・県の断定形が確認できない。使わない。
 *  - 「ふぐの取扱量が全国一位」も出典が県観光連盟。使わない。
 *  - ちしゃなますの由来、いとこ煮の名の由来はどちらも伝聞形。断定しない。
 *  - 山口の外郎を名古屋の外郎と比べて優劣をつけない。原料の違いだけを書く。
 *  - 見蘭牛は国・県の出典が取れず、牛も他7県で使用済みなので入れていない。
 *
 * 本番画像は public/assets/foods/yamaguchi/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：夏みかん菓子・外郎・ちしゃなます・ごぼう巻き・いとこ煮（店で食べられる品に差し替え）
 * - くじらの竜田揚げ：「近代捕鯨の基地」は書かない（入力メモにあるが、開いた公式ページで確かめていない。農水省ページの記述は「流通基地」）
 * - 宇部ラーメン：起源の店名（出典は「大阪屋」の店主が久留米で修行したことを起源とする）は書かない（店名の宣伝になる）
 * - 宇部ラーメン：「豚骨ラーメン発祥の地」（久留米について出典が「〜ともいわれる」と書く）は書かない（他県の称号で、この品の説明に要らない）
 * - 宇部ラーメン：店の数は「およそ15店」と書いたが、すぐ変わる数なので外してもよい
 * - 山賊焼き：店名は書かない（実質1店の名物なので、店名を出すと宣伝になる。tip の合言葉も店名を「山賊」に縮めた）
 * - 山賊焼き：甘辛だれ・竹串は書かない（出典に記述がない）
 * - とんちゃん鍋：具の名前（キャベツ・ニラ・もやし）は書かない（出典は「野菜」とだけ書く）
 * - とんちゃん鍋：「リトルプサン」の別名は書かない（韓国人街の呼び名で、料理の説明に要らない）
 * - とんちゃん鍋：発祥の店・年は書かない（出典に記述がない）
 * - ふく刺し（ふぐ刺し）：「ふぐ料理公認第一号店」の店名は書かない（店の宣伝になる）
 * - ふく刺し（ふぐ刺し）：伊藤博文の逸話は書かない（店名と結びついた話で、tip には解禁の年だけを書いた）
 * - ふく刺し（ふぐ刺し）：「ふぐの本場」は書かない（出典は「本場として知られています」と書くが、見出しに使う必要がない）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { yamaguchiSceneryTour } from './yamaguchiScenes';
import { YAMAGUCHI_FOOD_HULLS } from './yamaguchiFoodShapes.generated';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'wakame-musubi',
    level: 1,
    name: 'わかめむすび',
    shortName: 'わかめむすび',
    image: '/assets/foods/yamaguchi/01_wakame_musubi.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '海のみどりをまとった、萩のおむすび。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_4_yamaguchi.html',
    icon: { label: 'わかめむすび', shape: 'roe', baseColor: '#2a4a2a', accentColor: '#f4f0e8' },
    reward: {
      unlockScore: 200,
      area: '萩市・長門市',
      headline: '日本海側の萩や北浦、長門でつくられる、刻んだ乾燥わかめを全面にまぶしたおむすび。',
      tip: '農作業を共同でしていたころの昼食や間食としてつくられてきた。にぎった飯のまわりに刻みわかめをまぶすだけで、中に梅干しを入れることもある。',
      links: searchLinks('萩市', 'わかめむすび'),
    },
  },
  {
    id: 'kencho',
    level: 2,
    name: 'けんちょう',
    shortName: 'けんちょう',
    image: '/assets/foods/yamaguchi/02_kencho.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '炒めてから煮る、山口のふだんの味。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_2_yamaguchi.html',
    icon: { label: 'けんちょう', shape: 'pot', baseColor: '#e8e0cc', accentColor: '#d88a3a' },
    reward: {
      unlockScore: 500,
      area: '山口県内全域',
      headline: '豆腐と大根、にんじんを油で炒めてから煮る、山口県全域の家庭でつくられてきた煮もの。',
      tip: '豆腐はしっかり水を切り、大根とにんじんはくし形に切る。油で炒めてからだしと醤油、砂糖、みりんで煮含める。学校給食にも出る。',
      links: searchLinks('山口県', 'けんちょう'),
    },
  },
  {
    id: 'chicken-gobo',
    level: 3,
    name: 'チキンチキンごぼう',
    shortName: 'チキンごぼう',
    image: '/assets/foods/yamaguchi/03_chicken_gobo.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '給食から県じゅうに広がった甘辛。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_17_yamaguchi.html',
    icon: { label: 'チキンごぼう', shape: 'dumpling', baseColor: '#8a5020', accentColor: '#8ab04a' },
    reward: {
      unlockScore: 1000,
      area: '山口市（県内の学校給食から）',
      headline: '揚げた鶏肉とごぼうを甘辛いたれでからめた、学校給食から県じゅうに広まったおかず。',
      tip: '1995年ごろ、給食の献立が偏らないようにと家庭のレシピを募ったことから生まれた。鶏肉とささがきごぼうをそれぞれ揚げ、たれと枝豆を合わせてからめる。',
      links: searchLinks('山口県', 'チキンチキンごぼう'),
    },
  },
  {
    id: 'kujira-tatsuta',
    level: 4,
    name: 'くじらの竜田揚げ',
    shortName: 'くじら竜田揚げ',
    image: '/assets/foods/yamaguchi/04_kujira_tatsuta.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: 'くじらの赤肉を薄く切り、醤油・みりん・砂糖・生姜汁に漬けて下味をつけ、片栗粉をまぶしてからりと揚げる。噛むほどに味わいが深い。戦後の学校給食の定番で「給食界のチャンピオン」と呼ばれた。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_27_yamaguchi.html',
    icon: { label: '竜田揚げ', shape: 'dumpling', baseColor: '#7a3a24', accentColor: '#d9a35a' },
    reward: {
      unlockScore: 1800,
      area: '下関市／長門市',
      headline: '給食でおなじみだった揚げ物',
      tip: '長門は明治43年まで捕鯨で栄え、下関は江戸時代、とったくじらを流通させる基地だった。節分に「大きなものを食べて大きく年をとる」と、くじらを食べる地域もある。',
      links: searchLinks('下関市', 'くじらの竜田揚げ'),
      official: [{ label: '農林水産省「うちの郷土料理」くじらの竜田揚げ', url: 'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_27_yamaguchi.html' }],
    },
  },
  {
    id: 'ube-ramen',
    level: 5,
    name: '宇部ラーメン',
    shortName: '宇部ラーメン',
    image: '/assets/foods/yamaguchi/05_ube_ramen.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '豚骨をベースにした、においの強い濃厚なスープに中太の麺がからむ。細麺の博多系とは違い、久留米の豚骨ラーメンの流れをくむ。宇部市に根づき、市内のおよそ15店で食べられる。',
    source:
      'https://yamaguchi-tourism.jp/feature/gourmet_local',
    icon: { label: 'ラーメン', shape: 'ramen', baseColor: '#a8743e', accentColor: '#5c8a3a' },
    reward: {
      unlockScore: 3000,
      area: '宇部市',
      headline: '久留米の流れをくむ豚骨',
      tip: '山口県のラーメンの傾向は、東の醤油ラーメンと西の豚骨ラーメンに大きく分かれる。宇部ラーメンはその中でも異色とされる。',
      links: searchLinks('宇部市', '宇部ラーメン'),
      official: [{ label: 'おいでませ山口へ「山口県のご当地グルメ」', url: 'https://yamaguchi-tourism.jp/feature/gourmet_local' }],
    },
  },
  {
    id: 'sanzoku-yaki',
    level: 6,
    name: '山賊焼き',
    shortName: '山賊焼き',
    image: '/assets/foods/yamaguchi/06_sanzoku_yaki.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '若鶏を炭火で焼き上げる。岩国の山あいにある、のぼりや提灯で飾った日本家屋の店の名物で、県内はもちろん広島からも客が訪れる。こんがりと焼けた鶏にそのままかぶりつく。',
    source:
      'https://yamaguchi-tourism.jp/feature/localstore',
    icon: { label: '山賊焼き', shape: 'feast', baseColor: '#8a4a1e', accentColor: '#5f8f3a' },
    reward: {
      unlockScore: 4500,
      area: '岩国市',
      headline: '炭火で焼く若鶏にかぶりつく',
      tip: '地元では「免許を取ったら山賊」と合言葉のように言われ、車で出かける先として知られる。',
      links: searchLinks('岩国市', '山賊焼き'),
    },
  },
  {
    id: 'tonchan-nabe',
    level: 7,
    name: 'とんちゃん鍋',
    shortName: 'とんちゃん鍋',
    image: '/assets/foods/yamaguchi/07_tonchan_nabe.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'ホルモンを特製の甘辛だれで、水を使わず野菜から出る水分だけで炊き上げる。スープで炊く博多のもつ鍋とは違い、中央が少し深い専用の鉄板を使う。下関の韓国人街で生まれた。',
    source:
      'https://yamaguchi-tourism.jp/feature/gourmet_local',
    icon: { label: 'とんちゃん', shape: 'hotpot', baseColor: '#a3452a', accentColor: '#7fb54a' },
    reward: {
      unlockScore: 6500,
      area: '下関市',
      headline: '水を入れずに野菜の水分で炊く',
      tip: '主に焼肉店で出し、焼肉を食べたあとのもう一品として頼まれる。半世紀以上、地元で親しまれてきた。',
      links: searchLinks('下関市', 'とんちゃん鍋'),
      official: [{ label: 'おいでませ山口へ「山口県のご当地グルメ」', url: 'https://yamaguchi-tourism.jp/feature/gourmet_local' }],
    },
  },
  {
    id: 'uni-meshi',
    level: 8,
    name: 'うに飯',
    shortName: 'うに飯',
    image: '/assets/foods/yamaguchi/08_uni_meshi.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '磯の香りが立ちのぼる、萩の一膳。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_24_yamaguchi.html',
    icon: { label: 'うに飯', shape: 'roe', baseColor: '#f0e4c8', accentColor: '#e08a28' },
    reward: {
      unlockScore: 9000,
      area: '萩市',
      headline: '萩でとれるムラサキウニやアカウニを、昆布を敷いた米と炊きこむ、磯の香りが立つご飯。',
      tip: '米を洗って浸水させ、昆布を入れた鍋に生のうにを加えて炊く。蒸気が勢いよく立ったら火を止め、20分ほど蒸らす。',
      links: searchLinks('萩市', 'うに飯'),
    },
  },
  {
    id: 'fugu-karaage',
    level: 9,
    name: 'ふぐの唐揚げ',
    shortName: 'ふく唐揚げ',
    image: '/assets/foods/yamaguchi/09_fugu_karaage.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '衣はさくり、身はふっくらの下関。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_18_yamaguchi.html',
    icon: { label: 'ふく唐揚げ', shape: 'dumpling', baseColor: '#d8b060', accentColor: '#f0f0e8' },
    reward: {
      unlockScore: 12000,
      area: '下関市',
      headline: '下関でふぐは「ふく」と呼ばれる。骨つきのまま揚げた、身のふっくらした一品。',
      tip: '一口大に切って片栗粉をまぶし、180度の油で3、4分揚げる。レモンや塩、ポン酢で食べる。縁起をかついで福にちなみ「ふく」と呼んできた。',
      links: searchLinks('下関市', 'ふぐの唐揚げ'),
    },
  },
  {
    id: 'kawara-soba',
    level: 10,
    name: '瓦そば',
    shortName: '瓦そば',
    image: '/assets/foods/yamaguchi/10_kawara_soba.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '瓦の熱でこげる、茶そばの香ばしさ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_30_yamaguchi.html',
    icon: { label: '瓦そば', shape: 'ramen', baseColor: '#4a5a48', accentColor: '#e8c840' },
    reward: {
      unlockScore: 16000,
      area: '下関市豊浦町（川棚温泉）',
      headline: '熱した瓦の上に茶そばと錦糸卵、牛肉をのせ、温かいつゆにつけて食べる川棚温泉の名物。',
      tip: '瓦にふれた側の麺が香ばしく焼けていくので、食べ進むうちに味わいが変わる。きざみのり、ねぎ、レモン、もみじおろしをのせる。',
      links: searchLinks('下関市豊浦町', '瓦そば'),
    },
  },
  {
    id: 'iwakuni-zushi',
    level: 11,
    name: '岩国寿司',
    shortName: '岩国寿司',
    image: '/assets/foods/yamaguchi/11_iwakuni_zushi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '何段も重ねて押す、岩国の祝い寿司。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_5_yamaguchi.html',
    icon: { label: '岩国寿司', shape: 'feast', baseColor: '#f0e8d0', accentColor: '#d8b028' },
    reward: {
      unlockScore: 22000,
      area: '岩国市',
      headline: '木枠に寿司飯と具を何段も重ねて押した、殿様寿司とも呼ばれる岩国の祝いの寿司。',
      tip: '底にチシャの葉を敷き、魚のすり身を混ぜた寿司飯を詰め、具をのせてまた飯を重ねる。大勢ぶんを一度につくるため、木枠は60センチ四方になることもある。',
      links: searchLinks('岩国市', '岩国寿司'),
    },
  },
  {
    id: 'fuku-sashi',
    level: 12,
    name: 'ふく刺し（ふぐ刺し）',
    shortName: 'ふく刺し',
    image: '/assets/foods/yamaguchi/12_fuku_sashi.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: 'とらふぐを三枚におろし、身を斜めから薄く引いて皿に盛る。あさつきやもみじおろしを添え、ポンカンやダイダイを使ったポン酢で食べる。下関では福にちなんで、ふぐを「ふく」と呼ぶ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/43_16_yamaguchi.html',
    icon: { label: 'ふく刺し', shape: 'sashimi-platter', baseColor: '#eef2f4', accentColor: '#e0703a' },
    reward: {
      unlockScore: 30000,
      area: '下関市／萩市',
      headline: '身を薄く引いて盛る',
      tip: '天然のとらふぐの漁期は9月から3月で、冬の味覚とされる。明治21年、山口県でふぐ食が初めて解禁された。2016年に「下関ふく」が地理的表示（GI）に登録された。',
      links: searchLinks('下関市', 'ふく刺し'),
      official: [{ label: 'おいでませ山口へ「ふぐ」特集', url: 'https://yamaguchi-tourism.jp/feature/fugu' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const yamaguchiFoodStage: StageDefinition = {
  regionId: 'yamaguchi',
  regionName: '山口県',
  mode: 'food',
  title: '山口めしマージ',
  theme: {
    primaryColor: '#3a6a5a',
    secondaryColor: '#d8b050',
    accentColor: '#e8cc78',
    backgroundColor: '#12191a',
  },
  /** 背景ツアー「山口めぐり」。軸は一日の時間。夜明けの秋吉台から、夜の東後畑棚田へ。 */
  scenery: {
    tour: yamaguchiSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, YAMAGUCHI_FOOD_HULLS),
};
